import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Clipboard,
  Fingerprint,
  KeyRound,
  Link,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { FormEvent, useState } from "react";
import {
  createPasskeyDeviceLink,
  deleteMyPasskey,
  fetchMyPasskeys,
} from "../lib/auth-api";
import { useI18n } from "../i18n";
import type { AuthUser } from "../shared/auth";

export function MyDevicesPanel({ currentUser }: { currentUser: AuthUser }) {
  const { text, locale } = useI18n();
  const queryClient = useQueryClient();
  const [registrationUrl, setRegistrationUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState("");
  const [removalSuccess, setRemovalSuccess] = useState("");

  const passkeysQuery = useQuery({
    queryKey: ["my-passkeys"],
    queryFn: fetchMyPasskeys,
  });

  const linkMutation = useMutation({
    mutationFn: createPasskeyDeviceLink,
    onSuccess: (result) => {
      setRegistrationUrl(result.registrationUrl);
      setExpiresAt(result.expiresAt);
      setSuccess(text.devices.linkReady);
      queryClient.invalidateQueries({ queryKey: ["my-passkeys"] });
      window.setTimeout(() => setSuccess(""), 2200);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMyPasskey,
    onSuccess: async () => {
      setRemovalSuccess(text.devices.removed);
      await queryClient.invalidateQueries({ queryKey: ["my-passkeys"] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setSuccess("");
    setCopied(false);
    linkMutation.mutate();
  }

  async function copyLink() {
    await navigator.clipboard.writeText(registrationUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function removePasskey(credentialId: string, name: string) {
    const passkeys = passkeysQuery.data?.passkeys || [];
    const prompt =
      passkeys.length === 1
        ? text.devices.confirmDeleteLast
        : text.devices.confirmDelete;
    if (!window.confirm(prompt.replace("{name}", name))) return;
    setRemovalSuccess("");
    deleteMutation.mutate(credentialId);
  }

  return (
    <section className="devices-workspace">
      <div className="devices-summary">
        <div>
          <span className="eyebrow">{text.devices.personalAccess}</span>
          <h2>{text.devices.myDevices}</h2>
          <p>{text.devices.help}</p>
          <p className="devices-removal-note">{text.devices.deletionNote}</p>
        </div>
        <div className="devices-count">
          <KeyRound size={18} />
          <strong>
            {passkeysQuery.data
              ? passkeysQuery.data.passkeys.length
              : currentUser.passkeyCount}
          </strong>
          <span>Passkeys</span>
        </div>
      </div>

      <div className="devices-layout">
        <article className="devices-section">
          <div className="admin-heading">
            <div>
              <strong>{text.devices.registeredPasskeys}</strong>
              <span>{currentUser.email}</span>
            </div>
            <ShieldCheck size={18} />
          </div>

          {passkeysQuery.isLoading ? (
            <div className="admin-loading" />
          ) : passkeysQuery.data?.passkeys.length ? (
            <div className="device-list">
              {passkeysQuery.data.passkeys.map((passkey) => (
                <div className="device-row" key={passkey.credentialId}>
                  <div className="device-icon">
                    <Fingerprint size={18} />
                  </div>
                  <div className="device-details">
                    <strong>{passkey.name}</strong>
                    <span>
                      {deviceTypeLabel(passkey.deviceType, text)}
                      {passkey.backedUp
                        ? ` · ${text.devices.synced}`
                        : ` · ${text.devices.local}`}
                    </span>
                    <small>
                      {text.devices.created}{" "}
                      {formatDate(passkey.createdAt, locale, text)}
                      {" · "}
                      {text.devices.lastUse}{" "}
                      {formatDate(passkey.lastUsedAt, locale, text)}
                    </small>
                  </div>
                  <button
                    className="device-remove-button"
                    type="button"
                    title={text.devices.remove}
                    aria-label={`${text.devices.remove}: ${passkey.name}`}
                    disabled={deleteMutation.isPending}
                    onClick={() =>
                      removePasskey(passkey.credentialId, passkey.name)
                    }
                  >
                    <Trash2 size={14} />
                    {deleteMutation.isPending &&
                    deleteMutation.variables === passkey.credentialId
                      ? text.devices.removing
                      : text.devices.remove}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="device-empty">
              <KeyRound size={20} />
              <span>{text.devices.empty}</span>
            </div>
          )}
          {removalSuccess ? (
            <div className="device-success" role="status">
              <Check size={15} />
              {removalSuccess}
            </div>
          ) : null}
          {deleteMutation.isError ? (
            <div className="admin-error" role="alert">
              {formatError(deleteMutation.error, text)}
            </div>
          ) : null}
        </article>

        <article className="devices-section register-device-section">
          <div className="admin-heading">
            <div>
              <strong>{text.devices.addDevice}</strong>
              <span>{text.devices.addMeta}</span>
            </div>
            <Plus size={18} />
          </div>

          <form className="invite-form" onSubmit={submit}>
            <div className="device-link-help">
              <Link size={16} />
              <span>{text.devices.linkHelp}</span>
            </div>
            <button
              className="secondary-button invite-button"
              disabled={linkMutation.isPending}
            >
              <Fingerprint size={15} />
              {linkMutation.isPending
                ? text.devices.generating
                : text.devices.generateLink}
            </button>
          </form>

          {registrationUrl ? (
            <div className="invitation-result device-link-result">
              <span>
                {text.devices.oneTimeLink}
                {expiresAt
                  ? ` · ${text.devices.expires} ${formatDate(
                      expiresAt,
                      locale,
                      text,
                    )}`
                  : ""}
              </span>
              <div>
                <input readOnly value={registrationUrl} />
                <button
                  className="icon-button"
                  title={text.devices.copyLink}
                  onClick={copyLink}
                >
                  {copied ? <Check size={16} /> : <Clipboard size={16} />}
                </button>
              </div>
            </div>
          ) : null}
          {success ? (
            <div className="device-success">
              <Check size={15} />
              {success}
            </div>
          ) : null}
          {linkMutation.isError ? (
            <div className="admin-error">
              {formatError(linkMutation.error, text)}
            </div>
          ) : null}
        </article>
      </div>
    </section>
  );
}

function deviceTypeLabel(
  value: string,
  text: ReturnType<typeof useI18n>["text"],
) {
  if (value === "singleDevice") return text.devices.singleDevice;
  if (value === "multiDevice") return text.devices.multiDevice;
  return value || "Passkey";
}

function formatDate(
  value: string | null,
  locale: string,
  text: ReturnType<typeof useI18n>["text"],
) {
  if (!value) return text.devices.never;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatError(
  reason: unknown,
  text: ReturnType<typeof useI18n>["text"],
) {
  if (reason instanceof Error) {
    return reason.message;
  }
  return text.devices.fallbackError;
}
